import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import moment from 'moment';

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { ArrowBack, Delete, Send } from '@material-ui/icons';
import { grey, red } from '@material-ui/core/colors';
import ReactSelect from 'react-select';

import { useHasRealmRole, useAxios } from '../../../utils/hooks';
import { RealmRoles } from '../../../routes';

import { useStyles } from './styles';
import {
  getStatusChip,
  getPriorityChip,
  getCategoryChip,
  getOverdueChip,
  isTicketOverdue,
} from '../chips';

import Loader from '../../../components/Loader';
import FileUploader from '../../../components/FileUploader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import { getFileChip } from '../../../utils/getChip';
import {
  Ticket,
  TicketStatus,
  TicketStatusLabels,
} from '../../../models/ticket';

const statusOptions = Object.values(TicketStatus).map((value) => ({
  value,
  label: TicketStatusLabels[value],
}));

const HelpdeskShow: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const isAdmin = useHasRealmRole(RealmRoles.ADMIN);

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comment, setComment] = useState('');
  const [assignee, setAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [hoursSpent, setHoursSpent] = useState('');
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  const resetAlert = () => {
    setAlert({ isActive: false, type: 'info', message: 'none' });
  };

  const loadTicket = useCallback((data: Ticket) => {
    setTicket(data);
    setAssignee(data.assignee || '');
    setDueDate(data.dueDate ? moment(data.dueDate).format('YYYY-MM-DD') : '');
    setCompletedAt(
      data.completedAt
        ? moment(data.completedAt).format('YYYY-MM-DDTHH:mm')
        : '',
    );
    setHoursSpent(data.hoursSpent != null ? String(data.hoursSpent) : '');
  }, []);

  useEffect(() => {
    async function getTicket() {
      setLoading(true);

      try {
        const ticketResponse = await api.current?.get(`/helpdesk/${id}`);

        if (ticketResponse?.data) {
          loadTicket(ticketResponse.data);
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar ticket.',
        });
      } finally {
        setLoading(false);
      }
    }

    getTicket();
  }, [api, id, loadTicket]);

  const handleDownload = useCallback(
    (path: string) => {
      resetAlert();
      const formatPath = path.split('/');
      const filePath = formatPath.slice(0, -1);
      const fileName = formatPath[formatPath.length - 1];

      api.current
        ?.get(`files/${filePath.join('*')}*${fileName}`, {
          responseType: 'blob',
        })
        .then((response: any) => {
          const url = URL.createObjectURL(response.data);
          window.open(url, '_blank');
        })
        .catch(() =>
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao exibir arquivo.',
          }),
        );
    },
    [api],
  );

  const handleFileId = (fileId: string) => {
    if (fileId) {
      setFileIds((prev) => [...prev, fileId]);
    }
  };

  const handleAddComment = useCallback(async () => {
    if (!comment.trim()) {
      return;
    }

    resetAlert();
    setLoading(true);

    try {
      const response = await api.current?.post(`/helpdesk/${id}/comments`, {
        message: comment,
      });

      if (response?.data) {
        loadTicket(response.data);
      }

      setComment('');
      setAlert({
        isActive: true,
        type: 'success',
        message: 'Comentário adicionado.',
      });
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao adicionar comentário.',
      });
    } finally {
      setLoading(false);
    }
  }, [api, id, comment, loadTicket]);

  const handleAttachFiles = useCallback(async () => {
    if (!fileIds.length) {
      return;
    }

    resetAlert();
    setLoading(true);

    try {
      const response = await api.current?.post(`/helpdesk/${id}/files`, {
        files: fileIds,
      });

      if (response?.data) {
        loadTicket(response.data);
      }

      setFileIds([]);
      setAlert({
        isActive: true,
        type: 'success',
        message: 'Arquivos anexados.',
      });
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao anexar arquivos.',
      });
    } finally {
      setLoading(false);
    }
  }, [api, id, fileIds, loadTicket]);

  const handleUpdate = useCallback(
    async (payload: Partial<Ticket>) => {
      resetAlert();
      setLoading(true);

      try {
        const response = await api.current?.patch(`/helpdesk/${id}`, payload);

        if (response?.data) {
          loadTicket(response.data);
        }

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Ticket atualizado.',
        });
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao atualizar ticket.',
        });
      } finally {
        setLoading(false);
      }
    },
    [api, id, loadTicket],
  );

  const handleDelete = useCallback(async () => {
    resetAlert();
    setLoading(true);
    setDeleteOpen(false);

    try {
      await api.current?.delete(`/helpdesk/${id}`);
      history.push('/helpdesk');
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao excluir ticket.',
      });
      setLoading(false);
    }
  }, [api, id, history]);

  const handleStatusChange = (data: any) => {
    const status = data.value as TicketStatus;
    const completing =
      status === TicketStatus.RESOLVED || status === TicketStatus.CLOSED;

    if (completing && !completedAt) {
      setCompletedAt(moment().format('YYYY-MM-DDTHH:mm'));
    }

    handleUpdate({ status });
  };

  const handleDueDateBlur = () => {
    const current = ticket?.dueDate
      ? moment(ticket.dueDate).format('YYYY-MM-DD')
      : '';

    if (dueDate && dueDate !== current) {
      handleUpdate({ dueDate: dueDate as unknown as Date });
    }
  };

  const handleAssigneeBlur = () => {
    if (assignee !== (ticket?.assignee || '')) {
      handleUpdate({ assignee });
    }
  };

  const handleCompletedAtBlur = () => {
    const current = ticket?.completedAt
      ? moment(ticket.completedAt).format('YYYY-MM-DDTHH:mm')
      : '';

    if (completedAt && completedAt !== current) {
      handleUpdate({
        completedAt: moment(completedAt).toISOString() as unknown as Date,
      });
    }
  };

  const handleHoursSpentBlur = () => {
    const current = ticket?.hoursSpent != null ? String(ticket.hoursSpent) : '';
    const parsed = Number(hoursSpent);

    if (hoursSpent !== current && !Number.isNaN(parsed) && parsed >= 0) {
      handleUpdate({ hoursSpent: parsed });
    }
  };

  return (
    <>
      <Loader loading={loading} />

      <div className={classes.header}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => history.push('/helpdesk')}
        >
          Voltar
        </Button>

        {isAdmin && ticket && (
          <Button
            variant="contained"
            startIcon={<Delete />}
            style={{ background: red[700], color: '#fff' }}
            onClick={() => setDeleteOpen(true)}
          >
            Excluir
          </Button>
        )}
      </div>

      {ticket && (
        <>
          <Paper className={classes.paper}>
            <Typography
              variant="h6"
              className={classes.formTitles}
              gutterBottom
            >
              {ticket.title}
            </Typography>

            <Grid container spacing={1} className={classes.chips}>
              <Grid item>{getStatusChip(ticket.status)}</Grid>
              <Grid item>{getPriorityChip(ticket.priority)}</Grid>
              <Grid item>{getCategoryChip(ticket.category)}</Grid>
              {isTicketOverdue(ticket) && <Grid item>{getOverdueChip()}</Grid>}
            </Grid>

            <Typography variant="body1" className={classes.description}>
              {ticket.description}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Prontuários
                </Typography>
                <Typography variant="body2">
                  {ticket.recordNumbers && ticket.recordNumbers.length
                    ? ticket.recordNumbers.join(', ')
                    : '--'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Prazo de entrega
                </Typography>
                <Typography variant="body2">
                  {ticket.dueDate
                    ? moment(ticket.dueDate).format('DD/MM/YYYY')
                    : '--'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Responsável
                </Typography>
                <Typography variant="body2">
                  {ticket.assignee || '--'}
                </Typography>
              </Grid>

              {ticket.completedAt && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Concluído em
                  </Typography>
                  <Typography variant="body2">
                    {moment(ticket.completedAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
              )}

              {ticket.hoursSpent != null && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Horas gastas
                  </Typography>
                  <Typography variant="body2">{ticket.hoursSpent}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Solicitantes
                </Typography>
                <Grid container spacing={1}>
                  {ticket.requesters.map((requester) => (
                    <Grid item key={requester.email}>
                      <Chip label={`${requester.name} <${requester.email}>`} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            <Typography
              variant="body2"
              color="textSecondary"
              align="right"
              style={{ marginTop: '2%' }}
            >
              {`Criado por ${ticket.author} em ${moment(
                ticket.createdAt,
              ).format('DD/MM/YYYY HH:mm')}`}
            </Typography>
          </Paper>

          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.formTitles}>
              Anexos
            </Typography>

            <Grid container spacing={1} style={{ marginBottom: '1%' }}>
              {ticket.files && ticket.files.length ? (
                ticket.files
                  .filter((file) => file)
                  .map((file) => (
                    <Grid item key={file._id}>
                      {getFileChip(
                        file.mimetype,
                        file.originalname,
                        file.path,
                        handleDownload,
                      )}
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2">Nenhum anexo.</Typography>
                </Grid>
              )}
            </Grid>

            <FileUploader handleFileId={handleFileId} />

            {fileIds.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAttachFiles}
              >
                {`Anexar ${fileIds.length} arquivo(s) ao ticket`}
              </Button>
            )}
          </Paper>

          {isAdmin && (
            <Paper className={classes.paper}>
              <Typography variant="subtitle1" className={classes.formTitles}>
                Administração
              </Typography>

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Status
                  </Typography>
                  <ReactSelect
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === ticket.status,
                    )}
                    onChange={handleStatusChange}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Prazo de entrega"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dueDate}
                    onChange={(event) => setDueDate(event.target.value)}
                    onBlur={handleDueDateBlur}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Responsável"
                    fullWidth
                    value={assignee}
                    onChange={(event) => setAssignee(event.target.value)}
                    onBlur={handleAssigneeBlur}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Concluído em"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={completedAt}
                    onChange={(event) => setCompletedAt(event.target.value)}
                    onBlur={handleCompletedAtBlur}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Horas gastas"
                    type="number"
                    fullWidth
                    inputProps={{ min: 0, step: 0.5 }}
                    value={hoursSpent}
                    onChange={(event) => setHoursSpent(event.target.value)}
                    onBlur={handleHoursSpentBlur}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.formTitles}>
              Comentários
            </Typography>

            {ticket.comments && ticket.comments.length ? (
              ticket.comments.map((ticketComment, index) => (
                <Paper
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  elevation={2}
                  className={classes.comment}
                  style={{
                    background: ticketComment.fromAdmin ? '#e3f2fd' : grey[100],
                  }}
                >
                  <Typography variant="subtitle2">
                    {`${ticketComment.author}${
                      ticketComment.fromAdmin ? ' (Suporte)' : ''
                    }`}
                  </Typography>
                  <Typography variant="body2">
                    {ticketComment.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="right"
                    component="div"
                  >
                    {moment(ticketComment.createdAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2">Nenhum comentário ainda.</Typography>
            )}

            <Divider style={{ margin: '2% 0' }} />

            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={12} sm={10}>
                <TextField
                  label="Novo comentário"
                  fullWidth
                  multiline
                  rows={2}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<Send />}
                  onClick={handleAddComment}
                >
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Excluir ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este ticket? Esta ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancelar</Button>
          <Button color="secondary" onClick={handleDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default HelpdeskShow;
